import dayjs, { Dayjs } from 'dayjs';
import { Role, RoleModel, RoleType, UiRoleType } from '../../../services/RoleModel';
import { DateUtil } from '../../../util/DateUtil';

/**
 * Reine (Angular-freie) Aufbereitung der `RoleModel[]` für die zwei zusätzlichen Personen-Ansichten
 * (Verlauf + Verzweigungs-Zeitstrahl). Keine Formatierung/Übersetzung hier — die Komponenten
 * formatieren Labels/Farben selbst.
 */

/** Anzeige-Reihenfolge der Rollentypen (Belegungs-Strang zuerst, dann freie Rollen). */
export const ROLE_DISPLAY_ORDER: RoleType[] = [
	UiRoleType.RESIDENT,
	UiRoleType.VACANCY,
	UiRoleType.OWNER,
	UiRoleType.TENANT,
	UiRoleType.CONSUMPTION_INFO_RECEIVER,
];

/** Rollentypen des Belegungs-Strangs (Hauptlinie). */
export const OCCUPANCY_ROLE_TYPES: RoleType[] = [UiRoleType.RESIDENT, UiRoleType.VACANCY];

/** Rollentypen, die nur die Hausverwaltung pflegen darf — samt Person und Zustelladresse. */
export const PROTECTED_ROLE_TYPES: RoleType[] = [UiRoleType.OWNER, UiRoleType.CONSUMPTION_INFO_RECEIVER];

export function isProtectedRoleType(roleType: RoleType | null | undefined): boolean {
	return !!roleType && PROTECTED_ROLE_TYPES.includes(roleType);
}

// Platzhalter-Ids für Zeilen ohne echte Person: nur im Frontend, beim Speichern nie mitsenden.
// Erzeugt in `RoleModelService.getOrCreatePerson()`, das dieselben Konstanten nutzt.
export const SYNTHETIC_VACANCY_ID_PREFIX = 'vacancy';
export const SYNTHETIC_NEW_ID_PREFIX = 'new';

const SYNTHETIC_PERSON_ID_PATTERN = new RegExp(String.raw`^(${SYNTHETIC_VACANCY_ID_PREFIX}|${SYNTHETIC_NEW_ID_PREFIX})\d+$`);

/** True für Platzhalter-Ids (`vacancy0`, `new1`, …); echte Ids sind UUIDs und kollidieren nicht. */
export function isSyntheticPersonId(id: string | undefined | null): boolean {
	return !!id && SYNTHETIC_PERSON_ID_PATTERN.test(id);
}

/** True, wenn die Rolle ein echtes Enddatum hat (also geschlossen ist). */
export function roleHasEnd(role: Role): boolean {
	return !!role.validTo && !dayjs(role.validTo).isSame(DateUtil.maxDate, 'day');
}

/**
 * Stabiler, eindeutiger Track-Key für eine Rolle: bevorzugt die `id`; synthetische Einträge
 * ohne `id` (z. B. aufgefüllte Leerstands-Lücken) werden über Typ + Zeitraum eindeutig.
 */
export function roleTrackKey(role: Role): string {
	return role.id ?? `${role.roleType}|${role.validFrom?.valueOf() ?? ''}|${role.validTo?.valueOf() ?? ''}`;
}

function fromValue(role: Role): number {
	return role.validFrom ? dayjs(role.validFrom).valueOf() : 0;
}

// -------------------------------------------------------------------------------------------------
// Verlauf (rollen-/personenzentriert)
// -------------------------------------------------------------------------------------------------

export interface RoleHistoryEntry {
	roleModel: RoleModel;
	role: Role;
	isOpen: boolean;
}

export interface RoleHistoryGroup {
	roleType: RoleType;
	entries: RoleHistoryEntry[];
}

/** Gruppiert alle Rollen nach Typ; je Gruppe die Inhaber chronologisch (neueste zuerst). */
export function buildRoleHistoryByType(roleModels: RoleModel[]): RoleHistoryGroup[] {
	const groups: RoleHistoryGroup[] = ROLE_DISPLAY_ORDER.map((roleType): RoleHistoryGroup => ({ roleType, entries: [] }));
	for (const roleModel of roleModels ?? []) {
		for (const role of roleModel.roles ?? []) {
			const group = groups.find((candidate) => candidate.roleType === role.roleType);
			if (group) {
				group.entries.push({ roleModel, role, isOpen: !roleHasEnd(role) });
			}
		}
	}
	for (const group of groups) {
		group.entries.sort((a, b) => fromValue(b.role) - fromValue(a.role));
	}
	return groups.filter((group) => group.entries.length > 0);
}

export interface PersonHistoryEntry {
	roleModel: RoleModel;
	roles: Array<{ role: Role; isOpen: boolean }>;
}

/** Je Person deren Rollen chronologisch (neueste zuerst). */
export function buildRoleHistoryByPerson(roleModels: RoleModel[]): PersonHistoryEntry[] {
	return (roleModels ?? []).map((roleModel) => ({
		roleModel,
		roles: [...(roleModel.roles ?? [])].sort((a, b) => fromValue(b) - fromValue(a)).map((role) => ({ role, isOpen: !roleHasEnd(role) })),
	}));
}

// -------------------------------------------------------------------------------------------------
// Verzweigungs-Zeitstrahl (ereignisbasiert, horizontal)
// -------------------------------------------------------------------------------------------------

export interface TimelineSegment {
	roleModel: RoleModel;
	role: Role;
	isOpen: boolean;
	/** Index in `events` für validFrom. */
	startIndex: number;
	/** Index in `events` für validTo (offene Rolle → letzter Event-Index). */
	endIndex: number;
}

export interface TimelineLane {
	roleType: RoleType;
	segments: TimelineSegment[];
}

export interface BranchTimelineModel {
	/**
	 * Übergangs-Grenzen (gleichmäßige Spalten, kein Kalendermaßstab). Eine Grenze ist ein
	 * Einzug (`validFrom`) oder der Tag nach einem Auszug (`validTo + 1`). Angrenzende Perioden
	 * teilen sich dadurch eine Grenze → ihre Chips stoßen lückenlos aneinander.
	 */
	events: Dayjs[];
	/** Hauptlinie: Belegungs-Segmente (Nutzer/Bewohner ↔ Leerstand) chronologisch. */
	occupancy: TimelineSegment[];
	/** Nebenäste: freie Rollen je Typ. */
	lanes: TimelineLane[];
}

/** Baut das grenzenbasierte Zeitstrahl-Modell (Spalten = Wechsel-Ereignisse, nicht Kalenderabstand). */
export function buildBranchTimeline(roleModels: RoleModel[]): BranchTimelineModel {
	const allRoles: Array<{ roleModel: RoleModel; role: Role }> = [];
	for (const roleModel of roleModels ?? []) {
		for (const role of roleModel.roles ?? []) {
			if (role.validFrom) {
				allRoles.push({ roleModel, role });
			}
		}
	}
	if (allRoles.length === 0) {
		return { events: [], occupancy: [], lanes: [] };
	}

	const boundaries = new Map<string, Dayjs>();
	let hasOpen = false;
	for (const { role } of allRoles) {
		const from = dayjs(role.validFrom);
		boundaries.set(from.format('YYYY-MM-DD'), from);
		if (roleHasEnd(role)) {
			const exit = dayjs(role.validTo).add(1, 'day'); // Tag nach Auszug = Einzug der Folgeperiode
			boundaries.set(exit.format('YYYY-MM-DD'), exit);
		} else {
			hasOpen = true;
		}
	}
	const events = [...boundaries.values()].sort((a, b) => a.valueOf() - b.valueOf());
	// Synthetischer Endpunkt, damit offene Rollen (und Einzel-Grenzen) eine Spalte bekommen.
	const needsOpenTail = hasOpen || events.length === 1;
	const lastEvent = events.at(-1);
	if (needsOpenTail && lastEvent) {
		events.push(lastEvent.add(1, 'day'));
	}
	const lastIndex = events.length - 1;
	const indexOf = (date: Dayjs): number => {
		const target = dayjs(date);
		return Math.max(
			events.findIndex((event) => event.isSame(target, 'day')),
			0
		);
	};

	const toSegment = (entry: { roleModel: RoleModel; role: Role }): TimelineSegment => {
		const open = !roleHasEnd(entry.role);
		const startIndex = indexOf(dayjs(entry.role.validFrom));
		const endIndex = open ? lastIndex : Math.max(indexOf(dayjs(entry.role.validTo).add(1, 'day')), startIndex + 1);
		return { roleModel: entry.roleModel, role: entry.role, isOpen: open, startIndex, endIndex };
	};

	const occupancy = allRoles
		.filter((entry) => OCCUPANCY_ROLE_TYPES.includes(entry.role.roleType))
		.map(toSegment)
		.sort((a, b) => a.startIndex - b.startIndex);

	const freeTypes = ROLE_DISPLAY_ORDER.filter((roleType) => !OCCUPANCY_ROLE_TYPES.includes(roleType));
	const lanes: TimelineLane[] = freeTypes
		.map((roleType) => ({
			roleType,
			segments: allRoles
				.filter((entry) => entry.role.roleType === roleType)
				.map(toSegment)
				.sort((a, b) => a.startIndex - b.startIndex),
		}))
		.filter((lane) => lane.segments.length > 0);

	return { events, occupancy, lanes };
}
